import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';
import { BlobItem, BlobBrowserConfig } from './types';

export class BlobStorageService {
  private containerClient: ContainerClient;
  private config: BlobBrowserConfig;

  constructor(config: BlobBrowserConfig) {
    this.config = config;
    const blobServiceClient = new BlobServiceClient(
      `https://${config.accountName}.blob.core.windows.net`
    );
    this.containerClient = blobServiceClient.getContainerClient(config.containerName);
  }

  /**
   * Get all folders/files in a directory (used for prefetching and client-side operations)
   * 
   * This method fetches ALL items in a directory at once, which enables:
   * - Client-side pagination
   * - Real-time search and filtering without server round-trips
   * - Complete sorting capabilities
   * 
   * @param prefix - Directory prefix to list (empty string for root)
   * @returns Promise resolving to array of all blob items in the directory
   * @throws {Error} 'DIRECTORY_NOT_FOUND' if the directory doesn't exist
   * @throws {Error} 'FILE_PREFIX_NOT_FOUND' if the file prefix doesn't exist
   * @throws {Error} 'TOO_MANY_ITEMS:' if the directory contains more than a threshold
   * @throws {Error} 'CONTAINER_NOT_FOUND' if the storage container is inaccessible
   */
  async listAllBlobs(actualPrefix: string = ''): Promise<BlobItem[]> {
    try {
      // Use Azure's hierarchical listing to get directory structure
      const iterator = this.containerClient.listBlobsByHierarchy('/', {
        prefix: actualPrefix,
      }).byPage();

      const allItems: BlobItem[] = [];
      const ITEM_THRESHOLD = 5000;

      // Iterate through all pages to get complete directory listing
      for await (const page of iterator) {
        // Add directories (blob prefixes that represent folders)
        for (const prefix of page.segment.blobPrefixes || []) {
          allItems.push({
            name: this.getDisplayName(prefix.name, actualPrefix),
            isDirectory: true,
          });

          // Check if we've exceeded the threshold after processing each page
          // This allows us to bail out early rather than fetching all pages
          if (allItems.length > ITEM_THRESHOLD) {
            throw new Error(`TOO_MANY_ITEMS: Directory contains more than ${ITEM_THRESHOLD} items, which exceeds the limit for client-side pagination. Please use server-side pagination instead.`);
          }
        }

        // Add files (actual blob items)
        for (const blob of page.segment.blobItems || []) {
          allItems.push({
            name: this.getDisplayName(blob.name, actualPrefix),
            isDirectory: false,
            lastModified: blob.properties.lastModified,
            contentLength: blob.properties.contentLength,
            url: `https://${this.config.accountName}.blob.core.windows.net/${this.config.containerName}/${blob.name}`,
          });

          // Check if we've exceeded the threshold after processing each page
          // This allows us to bail out early rather than fetching all pages
          if (allItems.length > ITEM_THRESHOLD) {
            throw new Error(`TOO_MANY_ITEMS: Directory contains more than ${ITEM_THRESHOLD} items, which exceeds the limit for client-side pagination. Please use server-side pagination instead.`);
          }
        }
      }

      // Validate directory existence if no items found
      // This helps distinguish between empty folders and non-existent folders
      if (allItems.length === 0 && actualPrefix !== this.config.publicDirectory) {
        if (actualPrefix.endsWith('/')) {
          // This is a directory prefix - check if the directory exists (check for prefix without slash)
          const directoryNameWithoutSlash = actualPrefix.slice(0, -1);
          
          // Check if ANY blob exists with this prefix (this indicates the directory exists)
          const checkIterator = this.containerClient.listBlobsFlat({
            prefix: directoryNameWithoutSlash,
          }).byPage({ maxPageSize: 1 });
          
          const checkResponse = await checkIterator.next();
          const directoryExists = !checkResponse.done && checkResponse.value.segment.blobItems.length > 0;
          
          if (!directoryExists) {
            // Directory doesn't exist
            throw new Error('DIRECTORY_NOT_FOUND');
          }
          // If directoryExists is true, then it's just an empty folder (folder exists but no immediate children)
        } else {
          // This is a file prefix (no trailing slash) - file doesn't exist
          throw new Error('FILE_PREFIX_NOT_FOUND');
        }
      }
      return allItems;

    } catch (error: any) {
      console.error('Error listing all blobs:', error);
      
      // Re-throw our custom error as-is
      if (error.message === 'DIRECTORY_NOT_FOUND' || error.message === 'FILE_PREFIX_NOT_FOUND' || error.message.startsWith('TOO_MANY_ITEMS:')) {
        throw error;
      }
      
      // Check for Azure-specific errors and convert to user-friendly messages
      if (error.statusCode === 404 || error.code === 'ContainerNotFound') {
        throw new Error('CONTAINER_NOT_FOUND');
      }
      
      // Generic fallback error message
      throw new Error('Failed to load files');
    }
  }

  /**
   * Lists blobs with server-side pagination (used when prefetching fails or isn't suitable)
   * 
   * This method provides traditional pagination with continuation tokens, useful for:
   * - Large directories where prefetching all items would be too slow
   * - Fallback when client-side pagination isn't available
   * 
   * @param prefix - Directory prefix to list (empty string for root)
   * @param pageSize - Maximum number of items to return per page
   * @param continuationToken - Token for getting the next page of results
   * @returns Promise resolving to paginated result with items and pagination info
   * @throws {Error} 'FOLDER_NOT_FOUND' if the directory doesn't exist
   * @throws {Error} 'CONTAINER_NOT_FOUND' if the storage container is inaccessible
   */
  async listBlobs(actualPrefix: string = '', pageSize: number = 25, continuationToken?: string): Promise<{
    items: BlobItem[];
    hasNextPage: boolean;
    nextContinuationToken?: string;
  }> {
    try {
      // Get a single page of results using Azure's pagination
      const iterator = this.containerClient.listBlobsByHierarchy('/', {
        prefix: actualPrefix,
      }).byPage({
        maxPageSize: pageSize,
        continuationToken,
      });

      const response = await iterator.next();
      
      // Handle case where no results are available
      if (response.done) {
        return { items: [], hasNextPage: false };
      }

      const page = response.value;
      const items: BlobItem[] = [];

      // Add directories (blob prefixes that represent folders)
      for (const prefix of page.segment.blobPrefixes || []) {
        items.push({
          name: this.getDisplayName(prefix.name, actualPrefix),
          isDirectory: true,
        });
      }

      // Add files (actual blob items)
      for (const blob of page.segment.blobItems || []) {
        items.push({
          name: this.getDisplayName(blob.name, actualPrefix),
          isDirectory: false,
          lastModified: blob.properties.lastModified,
          contentLength: blob.properties.contentLength,
          // Generate direct download URL for the blob
          url: `https://${this.config.accountName}.blob.core.windows.net/${this.config.containerName}/${blob.name}`,
        });
      }

      // Validate directory existence if no items found
      if (items.length === 0 && actualPrefix !== this.config.publicDirectory) {
        if (actualPrefix.endsWith('/')) {
          // This is a directory prefix - check if the directory exists (check for prefix without slash)
          const directoryNameWithoutSlash = actualPrefix.slice(0, -1);
          
          // Check if ANY blob exists with this prefix (this indicates the directory exists)
          const checkIterator = this.containerClient.listBlobsFlat({
            prefix: directoryNameWithoutSlash,
          }).byPage({ maxPageSize: 1 });
          
          const checkResponse = await checkIterator.next();
          const directoryExists = !checkResponse.done && checkResponse.value.segment.blobItems.length > 0;
          
          if (!directoryExists) {
            // Directory doesn't exist
            throw new Error('DIRECTORY_NOT_FOUND');
          }
          // If directoryExists is true, then it's just an empty folder (folder exists but no immediate children)
        } else {
          // This is a file prefix (no trailing slash) - file doesn't exist
          throw new Error('FILE_PREFIX_NOT_FOUND');
        }
      }

      return {
        items,
        hasNextPage: !!page.continuationToken, // Convert to boolean - true if more pages available
        nextContinuationToken: page.continuationToken,
      };
    } catch (error: any) {
      console.error('Error listing blobs:', error);
      
      // Re-throw our custom error as-is
      if (error.message === 'DIRECTORY_NOT_FOUND' || error.message === 'FILE_PREFIX_NOT_FOUND') {
        throw error;
      }
      
      // Check for Azure-specific errors and convert to user-friendly messages
      if (error.statusCode === 404 || error.code === 'ContainerNotFound') {
        throw new Error('CONTAINER_NOT_FOUND');
      }
      
      // Generic fallback error message
      throw new Error('Failed to load files');
    }
  }

  /**
   * Converts full blob names to display names for the current directory
   * 
   * This method removes the parent directory path from blob names to show only
   * the relevant filename or folder name in the current directory view.
   * 
   * Example transformations (assuming actualPrefix = "download/docs/"):
   * - "download/file.pdf" -> "file.pdf" (file in parent directory)
   * - "download/folder/" -> "folder/" (folder in parent directory)  
   * - "download/docs/nested/" -> "nested/" (subfolder)
   * - "download/docs/document.pdf" -> "document.pdf" (file in current directory)
   * 
   * @param fullName - Complete blob name as stored in Azure (e.g., "download/docs/file.pdf")
   * @param actualPrefix - Current directory prefix including public directory (e.g., "download/docs/")
   * @returns Clean display name relative to parent directory for UI display
   * @private
   */
  private getDisplayName(fullName: string, actualPrefix: string): string {
    const currentParts = actualPrefix.trim().split('/');
    currentParts.pop();
    const currentDirectoryPrefix = currentParts.join('/') + '/';

    let displayName = fullName;
    if (displayName.startsWith(currentDirectoryPrefix)) {
      // Remove the current directory prefix if it exists
      displayName = displayName.substring(currentDirectoryPrefix.length);
    }
    else {
      // This case should not happen in normal usage.
    }
    
    // Remove trailing slash if present (for directories)
    if (displayName.endsWith('/')) {
      displayName = displayName.slice(0, -1);
    }
    
    return displayName;
  }

  /**
   * Extracts the current directory prefix from the browser URL
   * 
   * @returns Current prefix from URL or empty string for root directory
   */
  getPrefix(): string {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('prefix') || '';
  }

  /**
   * Calculates the parent directory prefix for navigation purposes
   * 
   * This method enables "back" navigation by determining the parent directory
   * of the current location. It handles various path formats consistently:
   * - "folder/" -> "" (root)
   * - "folder/subfolder/" -> "folder/"
   * - "folder/file.txt" -> "folder/"
   * - "" -> "" (already at root)
   * 
   * Assumes the current prefix is already processed, i.e., it does not have any
   * leading / and at most one trailing slash.
   * 
   * @param currentPrefix - Current directory prefix (may or may not end with '/')
   * @returns Parent directory prefix with trailing slash, or empty string if at root
   */
  getParentPrefix(currentPrefix: string): string {
    let normalizedPrefix = currentPrefix.trim();
    console.log(normalizedPrefix);
    // Handle empty or whitespace-only input (already at root)
    if (!normalizedPrefix) {
      return this.config.publicDirectory;
    }
    
    // Remove trailing slash
    if (normalizedPrefix.endsWith('/')) {
      normalizedPrefix = normalizedPrefix.slice(0, -1);
    }
    
    // If nothing left after normalization, we're at root
    if (!normalizedPrefix) {
      return this.config.publicDirectory;
    }
    
    // Split by '/' and remove the last segment to get parent path
    const pathSegments = normalizedPrefix.split('/');
    pathSegments.pop(); // Remove the last segment (current folder or file)
    
    // If no segments remain, we're going back to root
    if (pathSegments.length === 0) {
      return this.config.publicDirectory;
    }
    
    // Reconstruct parent path with trailing slash for consistency
    return pathSegments.join('/') + '/';
  }
}
